import { payrollRunApiBaseUrl } from '../../shared/env';
import { refreshAccessToken } from '../../shared/tokenRefresh';
import type { PayrollHistoryImportGateway } from './PayrollHistoryImportGateway';
import {
  NO_CAPABILITIES, PayrollHistoryApiError,
  type BatchDetail, type BatchListQuery, type BatchPage, type Capabilities, type CommitRequest, type DownloadedFile,
  type EmployeeOption, type HistoricalRunDetail, type HistoricalRunPage, type Preview, type PreviewRunDetail,
  type SaveColumnMappingRequest, type SaveMappingsRequest, type SavedMappingProfile, type Structure, type TemplateKind,
  type ValidationSummary,
} from './types';

const IMPORTS = '/api/payroll-history-imports';
const HISTORY = '/api/payroll-history';

/**
 * Sends a request with the session cookie; a 401 triggers ONE shared token refresh and a retry (same
 * behaviour as createJsonClient). Used for JSON, multipart and file downloads so none of them regress to the
 * old "no refresh" upload behaviour. Non-2xx responses become a PayrollHistoryApiError that also carries the
 * problem body's `code`.
 */
async function send(path: string, init: RequestInit, signal?: AbortSignal): Promise<Response> {
  const doFetch = () =>
    fetch(`${payrollRunApiBaseUrl}${path}`, { ...init, ...(signal ? { signal } : {}), credentials: 'include' });

  let response = await doFetch();
  if (response.status === 401 && (await refreshAccessToken())) {
    response = await doFetch();
  }
  if (!response.ok) {
    const problem = (await response.json().catch(() => null)) as { detail?: string; code?: string } | null;
    const detail = typeof problem?.detail === 'string' && problem.detail.trim() !== '' ? problem.detail : undefined;
    throw new PayrollHistoryApiError(
      response.status,
      detail ?? `Request to ${path} failed with ${response.status}`,
      problem?.code,
      detail !== undefined,
    );
  }
  return response;
}

async function json<T>(path: string, init: RequestInit = {}, signal?: AbortSignal): Promise<T> {
  // A FormData body must NOT get a JSON Content-Type: the browser sets multipart/form-data + boundary itself.
  const hasJsonBody = init.body !== undefined && init.body !== null && !(init.body instanceof FormData);
  const response = await send(
    path,
    hasJsonBody ? { ...init, headers: { 'Content-Type': 'application/json', ...init.headers } } : init,
    signal,
  );
  const text = await response.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

function fileNameFromDisposition(header: string | null, fallback: string): string {
  if (!header) return fallback;
  const star = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1]);
    } catch {
      /* fall through to the plain form */
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain?.[1] ?? fallback;
}

async function download(path: string, fallbackName: string): Promise<DownloadedFile> {
  const response = await send(path, { method: 'GET' });
  return {
    blob: await response.blob(),
    fileName: fileNameFromDisposition(response.headers.get('Content-Disposition'), fallbackName),
  };
}

const enc = encodeURIComponent;

/** Used only when the server sends no Content-Disposition file name. */
const TEMPLATE_FALLBACK_NAMES: Record<TemplateKind, string> = {
  template: 'escochex-payroll-history-template.zip',
  sample: 'escochex-payroll-history-sample.zip',
  instructions: 'escochex-payroll-history-import-instructions.md',
  'standard-template': 'EPN_Standard_Payroll_History_Template.xlsx',
  'standard-example': 'EPN_Standard_Payroll_History_Example.xlsx',
};

export class HttpPayrollHistoryImportGateway implements PayrollHistoryImportGateway {
  async getCapabilities(signal?: AbortSignal): Promise<Capabilities> {
    try {
      const caps = await json<Capabilities>(`${IMPORTS}/capabilities`, {}, signal);
      // An older server has no provider list: show none rather than guess one.
      return { ...caps, providers: caps.providers ?? [] };
    } catch (err) {
      // Contract: a caller without `read` gets 403 — that means "no access", not a failure to display.
      if (err instanceof PayrollHistoryApiError && err.status === 403) return NO_CAPABILITIES;
      throw err;
    }
  }

  downloadTemplate(kind: TemplateKind): Promise<DownloadedFile> {
    return download(`${IMPORTS}/templates/${kind}`, TEMPLATE_FALLBACK_NAMES[kind]);
  }

  startImport(sourceSystem: string, provider?: string): Promise<BatchDetail> {
    const body = provider ? { sourceSystem, provider } : { sourceSystem };
    return json(IMPORTS, { method: 'POST', body: JSON.stringify(body) });
  }

  uploadFile(batchId: string, file: File): Promise<BatchDetail> {
    const form = new FormData();
    form.append('file', file);
    return json(`${IMPORTS}/${enc(batchId)}/file`, { method: 'POST', body: form });
  }

  getBatch(batchId: string, signal?: AbortSignal): Promise<BatchDetail> {
    return json(`${IMPORTS}/${enc(batchId)}`, {}, signal);
  }

  getValidation(batchId: string, signal?: AbortSignal): Promise<ValidationSummary> {
    return json(`${IMPORTS}/${enc(batchId)}/validation`, {}, signal);
  }

  getStructure(batchId: string, sheet?: string, signal?: AbortSignal): Promise<Structure> {
    const query = sheet ? `?sheet=${enc(sheet)}` : '';
    return json(`${IMPORTS}/${enc(batchId)}/structure${query}`, {}, signal);
  }

  saveColumnMapping(batchId: string, request: SaveColumnMappingRequest): Promise<ValidationSummary> {
    return json(`${IMPORTS}/${enc(batchId)}/column-mapping`, { method: 'PUT', body: JSON.stringify(request) });
  }

  listMappingProfiles(provider?: string, signal?: AbortSignal): Promise<SavedMappingProfile[]> {
    const query = provider ? `?provider=${enc(provider)}` : '';
    return json(`${IMPORTS}/mapping-profiles${query}`, {}, signal);
  }

  searchEmployeeOptions(batchId: string, search: string, signal?: AbortSignal): Promise<EmployeeOption[]> {
    return json(`${IMPORTS}/${enc(batchId)}/employee-options?search=${enc(search)}`, {}, signal);
  }

  saveMappings(batchId: string, request: SaveMappingsRequest): Promise<ValidationSummary> {
    return json(`${IMPORTS}/${enc(batchId)}/employee-mappings`, { method: 'PUT', body: JSON.stringify(request) });
  }

  revalidate(batchId: string): Promise<ValidationSummary> {
    return json(`${IMPORTS}/${enc(batchId)}/revalidate`, { method: 'POST' });
  }

  getPreview(batchId: string, signal?: AbortSignal): Promise<Preview> {
    return json(`${IMPORTS}/${enc(batchId)}/preview`, {}, signal);
  }

  getPreviewRun(batchId: string, sourcePayrollRunId: string, signal?: AbortSignal): Promise<PreviewRunDetail> {
    return json(`${IMPORTS}/${enc(batchId)}/preview/runs/${enc(sourcePayrollRunId)}`, {}, signal);
  }

  commit(batchId: string, request: CommitRequest): Promise<BatchDetail> {
    return json(`${IMPORTS}/${enc(batchId)}/commit`, { method: 'POST', body: JSON.stringify(request) });
  }

  cancel(batchId: string): Promise<BatchDetail> {
    return json(`${IMPORTS}/${enc(batchId)}/cancel`, { method: 'POST' });
  }

  downloadErrorReport(batchId: string): Promise<DownloadedFile> {
    return download(`${IMPORTS}/${enc(batchId)}/error-report`, `validation-report-${batchId}.csv`);
  }

  exportHistory(): Promise<DownloadedFile> {
    return download(`${HISTORY}/export`, 'epn-payroll-history.xlsx');
  }

  listBatches(query: BatchListQuery, signal?: AbortSignal): Promise<BatchPage> {
    const params = new URLSearchParams();
    if (query.status) params.set('status', query.status);
    if (query.sourceSystem) params.set('sourceSystem', query.sourceSystem);
    if (query.from) params.set('from', query.from);
    if (query.to) params.set('to', query.to);
    if (query.importedBy) params.set('importedBy', query.importedBy);
    params.set('page', String(query.page));
    params.set('pageSize', String(query.pageSize));
    return json(`${IMPORTS}?${params.toString()}`, {}, signal);
  }

  listHistoricalRuns(page: number, pageSize: number, signal?: AbortSignal): Promise<HistoricalRunPage> {
    return json(`${HISTORY}/runs?page=${page}&pageSize=${pageSize}`, {}, signal);
  }

  getHistoricalRun(runId: string, signal?: AbortSignal): Promise<HistoricalRunDetail> {
    return json(`${HISTORY}/runs/${enc(runId)}`, {}, signal);
  }
}
